import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import api, { BACKEND } from '../../services/api'
import { getSocket } from '../../services/socket'
import { useAuth } from '../../context/AuthContext'
import { clearBadge } from '../../services/notifications'
import CatalogoPicker from '../../components/CatalogoPicker'
import toast from 'react-hot-toast'

function mediaUrl(url) {
  if (!url) return ''
  const resolvedUrl = url.startsWith('http') ? url : `${BACKEND}${url}`
  // Las versiones previas de la APK guardaron en caché respuestas de fotos
  // bloqueadas por CORP. Esta versión usa una clave estable nueva solo en
  // Capacitor para obtener las imágenes con las cabeceras ya corregidas.
  if (!Capacitor.isNativePlatform()) return resolvedUrl
  return `${resolvedUrl}${resolvedUrl.includes('?') ? '&' : '?'}client=mixcrm-android-v2`
}

// Convierte formato WhatsApp a HTML: *bold*, _italic_, ~strike~, ```code```
function renderTexto(texto) {
  if (!texto) return null
  const html = texto
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/```([\s\S]*?)```/g, '<code>$1</code>')
    .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_\n]+)_/g, '<em>$1</em>')
    .replace(/~([^~\n]+)~/g, '<s>$1</s>')
    .replace(/\n/g, '<br/>')
  return <span dangerouslySetInnerHTML={{ __html: html }} />
}

const PRIORIDADES = { baja: '🟢', normal: '🔵', alta: '🟡', urgente: '🔴' }

const PRIO_META = {
  baja:    { label: 'Baja',    color: '#22c55e' },
  normal:  { label: 'Normal',  color: '#3b82f6' },
  alta:    { label: 'Alta',    color: '#f59e0b' },
  urgente: { label: 'Urgente', color: '#ef4444' },
}

// Número a mostrar. Para @lid con número real resuelto (telefono ≠ id del lid) se muestra;
// si no hay número real (privacidad WhatsApp), "Número oculto".
function numeroMostrar(c) {
  if (!c) return ''
  const lidId = c.jid?.includes('@lid') ? c.jid.split('@')[0] : null
  if (c.telefono && c.telefono !== lidId) return `+${c.telefono}`
  if (lidId) return 'Número oculto'
  return c.telefono ? `+${c.telefono}` : 'Sin número'
}

// Nombre a mostrar: si el "nombre" es solo dígitos (un id/número, no un nombre real),
// mostrar el número real si lo hay, o "Contacto".
function nombreMostrar(c) {
  if (!c) return 'Sin nombre'
  const n = c.nombre
  if (n && /^\d{6,}$/.test(n)) {
    const num = numeroMostrar(c)
    return (num && num !== 'Número oculto' && num !== 'Sin número') ? num : 'Contacto'
  }
  return n || numeroMostrar(c)
}

const MENU_ITEM_STYLE = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', padding: '9px 14px', border: 'none', background: 'none',
  cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text)',
  textAlign: 'left', transition: 'background 0.1s',
}

export default function Inbox() {
  const { usuario } = useAuth()
  const [searchParams] = useSearchParams()
  const [chats, setChats] = useState([])
  const [chatActivo, setChatActivo] = useState(null)
  const [mensajes, setMensajes] = useState([])
  const [notas, setNotas] = useState([])
  const [historial, setHistorial] = useState([])
  const [texto, setTexto] = useState('')
  const [buscar, setBuscar] = useState('')
  const [tab, setTab] = useState('todos')
  const [filtroExtra, setFiltroExtra] = useState('todos')
  const [enviando, setEnviando] = useState(false)
  const [respuestasRapidas, setRespuestasRapidas] = useState([])
  const [showRapidas, setShowRapidas] = useState(false)
  const [showNotas, setShowNotas] = useState(false)
  const [showCatalogo, setShowCatalogo] = useState(false)
  const [showHistorial, setShowHistorial] = useState(false)
  const [nuevaNota, setNuevaNota] = useState('')
  const [archivoPreview, setArchivoPreview] = useState(null)
  const [vista, setVista] = useState('inbox') // 'inbox' | 'archivados' | 'papelera'
  const [menuChat, setMenuChat] = useState(null) // chatId con menú abierto
  const [menuHeader, setMenuHeader] = useState(false) // menú ⋮ de la cabecera del chat
  const menuRef = useRef(null)
  const fileInputRef    = useRef(null)
  const galleryInputRef = useRef(null)
  const endRef = useRef(null)
  const messagesAreaRef = useRef(null)
  const textareaRef = useRef(null)
  const initialMsgLoad = useRef(false)

  // ── Inbox selector (negocio / número) ──
  const [inboxFiltro, setInboxFiltro] = useState(() => {
    const neg = searchParams.get('negocio')
    const negNombre = searchParams.get('nombre')
    if (neg) return { tipo: 'negocio', id: neg, nombre: negNombre || 'Negocio' }
    return { tipo: 'todos', id: null, nombre: 'Todos los chats' }
  })
  const [negocios, setNegocios] = useState([])
  const [showInboxMenu, setShowInboxMenu] = useState(false)

  // ── Panel derecho: tabs ──
  const [tabDerecho, setTabDerecho] = useState('contacto') // contacto | notas | seguimiento

  // ── Crear deal desde chat ──
  const [showCrearDeal, setShowCrearDeal] = useState(false)
  const [pipelines, setPipelines] = useState([])
  const [dealForm, setDealForm] = useState({ titulo: '', pipelineId: '', etapaId: '', valor: '' })
  const [etapas, setEtapas] = useState([])
  const [isMobile, setIsMobile] = useState(false)
  const [showRightPanel, setShowRightPanel] = useState(true)
  const [lightboxImg, setLightboxImg] = useState(null)
  const [showEditContacto, setShowEditContacto] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [editGuardando, setEditGuardando] = useState(false)
  const [editTagInput, setEditTagInput] = useState('')

  useEffect(() => { return () => clearBadge() }, [])

  // Cargar negocios para el selector de inbox
  useEffect(() => {
    api.get('/negocios').then(r => setNegocios(r.data)).catch(() => {})
    api.get('/pipelines').then(r => setPipelines(r.data)).catch(() => {})
  }, [])

  // Cargar etapas cuando cambia el pipeline en el form de deal
  useEffect(() => {
    if (!dealForm.pipelineId) { setEtapas([]); return }
    const pip = pipelines.find(p => p.id === dealForm.pipelineId)
    setEtapas(pip?.Etapas || [])
  }, [dealForm.pipelineId, pipelines])

  const cargarChats = useCallback(async () => {
    const params = {}
    if (vista !== 'inbox') {
      params.vista = vista
    } else {
      if (tab === 'mios')   params.soloMios = 'true'
      if (tab === 'espera') params.status = 'en_espera'
      if (inboxFiltro.tipo === 'sesion')  params.sesionId  = inboxFiltro.id
      if (inboxFiltro.tipo === 'negocio') params.negocioId = inboxFiltro.id
    }
    const { data } = await api.get('/chats', { params })
    setChats(data)
  }, [tab, inboxFiltro, vista])

  useEffect(() => { cargarChats() }, [cargarChats])

  useEffect(() => {
    api.get('/respuestas/rapidas').then(r => setRespuestasRapidas(r.data))
  }, [])

  useEffect(() => {
    // Registrar qué chat está abierto para que AppLayout no lo notifique
    localStorage.setItem('inbox_chat_activo', chatActivo?.id || '')
    return () => localStorage.removeItem('inbox_chat_activo')
  }, [chatActivo?.id])

  useEffect(() => {
    if (chatActivo) {
      initialMsgLoad.current = true
      api.get(`/mensajes/${chatActivo.id}`).then(r => setMensajes(r.data))
      api.get(`/chats/${chatActivo.id}/notas`).then(r => setNotas(r.data))
      api.post(`/chats/${chatActivo.id}/marcar-leido`).catch(() => {})
      // Historial de conversaciones anteriores del mismo contacto
      if (chatActivo.Contacto?.id) {
        api.get(`/chats`, { params: { contactoId: chatActivo.Contacto.id, excluir: chatActivo.id } })
          .then(r => setHistorial(r.data || []))
          .catch(() => setHistorial([]))
      }
    }
  }, [chatActivo?.id])

  useEffect(() => {
    if (!messagesAreaRef.current) return
    if (initialMsgLoad.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight
      initialMsgLoad.current = false
    } else {
      endRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [mensajes])

  // Detectar mobile y escuchar resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Cerrar panel derecho al cambiar de chat
  useEffect(() => { setShowRightPanel(false) }, [chatActivo?.id])

  // En mobile: interceptar el botón Atrás del navegador/teléfono para volver
  // a la lista de chats en vez de salir del inbox
  useEffect(() => {
    if (!chatActivo || !isMobile) return
    window.history.pushState({ chatOpen: true }, '')
    const onBack = () => {
      setChatActivo(null)
      setShowRightPanel(false)
    }
    window.addEventListener('popstate', onBack)
    return () => window.removeEventListener('popstate', onBack)
  }, [chatActivo, isMobile])

  // Cerrar lightbox con Escape
  useEffect(() => {
    if (!lightboxImg) return
    const onKey = e => { if (e.key === 'Escape') setLightboxImg(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightboxImg])

  // Tiempo real + notificaciones
  useEffect(() => {
    const sock = getSocket()
    if (!sock) return

    const onMsg = ({ chatId, mensaje }) => {
      if (chatActivo?.id === chatId) {
        setMensajes(prev => [...prev, mensaje])
      }
      cargarChats()
    }

    const onChatUpd = (data) => {
      setChats(prev => prev.map(c => c.id === data.chatId ? { ...c, ...data } : c))
      if (chatActivo?.id === data.chatId) setChatActivo(prev => ({ ...prev, ...data }))
    }

    // Cuando la media se descargó del servidor WAHA y ya tiene URL local
    const onMediaGuardada = ({ mensajeId, mediaUrl: url }) => {
      setMensajes(prev => prev.map(m => m.id === mensajeId ? { ...m, mediaUrl: url } : m))
    }

    const onChatArchivado = ({ chatId }) => {
      setChats(prev => prev.filter(c => c.id !== chatId))
      if (chatActivo?.id === chatId) setChatActivo(null)
    }

    const onMsgEstado = ({ mensajeId, estado }) => {
      setMensajes(prev => prev.map(m => m.id === mensajeId ? { ...m, estado } : m))
    }

    const onContactoUpd = ({ contactoId, ...campos }) => {
      // campos puede traer avatar, nombre, telefono, jid
      const patch = c => (c?.id === contactoId ? { ...c, ...campos } : c)
      setChats(prev => prev.map(ch => ch.Contacto?.id === contactoId
        ? { ...ch, Contacto: patch(ch.Contacto) } : ch))
      setChatActivo(prev => prev?.Contacto?.id === contactoId
        ? { ...prev, Contacto: patch(prev.Contacto) } : prev)
    }

    sock.on('mensaje-nuevo', onMsg)
    sock.on('chat-update', onChatUpd)
    sock.on('media-guardada', onMediaGuardada)
    sock.on('mensaje-estado', onMsgEstado)
    sock.on('contacto-actualizado', onContactoUpd)
    sock.on('chat-escalado', ({ chatId }) => {
      toast(`💬 Chat escalado a humano`, { icon: '🔔' })
      cargarChats()
    })
    // Distinto de escalar: el bot NO se apagó, solo está fallando. Avisa para
    // que alguien pueda entrar si hace falta, sin decir que el chat ya es suyo.
    sock.on('bot-con-fallos', () => {
      toast('El bot no logra responder en un chat — sigue activo, pero conviene revisarlo', { icon: '⚠️' })
      cargarChats()
    })
    sock.on('chat-archivado', onChatArchivado)
    sock.on('chat-eliminado', onChatArchivado)
    return () => {
      sock.off('mensaje-nuevo', onMsg)
      sock.off('chat-update', onChatUpd)
      sock.off('media-guardada', onMediaGuardada)
      sock.off('mensaje-estado', onMsgEstado)
      sock.off('chat-escalado')
      sock.off('bot-con-fallos')
      sock.off('chat-archivado', onChatArchivado)
      sock.off('chat-eliminado', onChatArchivado)
      sock.off('contacto-actualizado', onContactoUpd)
    }
  }, [chatActivo, cargarChats, chats])

  const enviar = async () => {
    if (!texto.trim() || !chatActivo || enviando) return
    setEnviando(true)
    try {
      await api.post('/mensajes', { chatId: chatActivo.id, texto })
      setTexto('')
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al enviar')
    } finally { setEnviando(false) }
  }

  const seleccionarArchivo = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const tipo = file.type.startsWith('image/') ? 'image'
      : file.type.startsWith('video/') ? 'video'
      : file.type.startsWith('audio/') ? 'audio' : 'document'
    setArchivoPreview({ file, url: URL.createObjectURL(file), tipo })
    e.target.value = ''
  }

  const enviarArchivo = async () => {
    if (!archivoPreview || !chatActivo || enviando) return
    setEnviando(true)
    try {
      const form = new FormData()
      form.append('file', archivoPreview.file)
      form.append('chatId', chatActivo.id)
      if (texto.trim()) form.append('texto', texto)
      // No fijar Content-Type: axios/navegador lo ponen con el boundary correcto
      await api.post('/mensajes/upload', form)
      setArchivoPreview(null)
      setTexto('')
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al enviar archivo')
    } finally { setEnviando(false) }
  }

  const cancelarArchivo = () => {
    if (archivoPreview?.url) URL.revokeObjectURL(archivoPreview.url)
    setArchivoPreview(null)
  }

  const manejarPaste = (e) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (!file) return
        // Nombrar el archivo con timestamp para que sea identificable
        const ext = item.type === 'image/png' ? 'png' : item.type === 'image/gif' ? 'gif' : 'jpg'
        const named = new File([file], `imagen-${Date.now()}.${ext}`, { type: item.type })
        setArchivoPreview({ file: named, url: URL.createObjectURL(named), tipo: 'image' })
        return
      }
    }
  }

  const tomar = async () => {
    await api.post(`/chats/${chatActivo.id}/tomar`)
    toast.success('Tomaste el chat')
    cargarChats()
  }

  const activarBot = async () => {
    await api.post(`/chats/${chatActivo.id}/bot`)
    toast('🤖 Bot reactivado')
    cargarChats()
  }

  const cerrar = async () => {
    if (!window.confirm('¿Cerrar este chat?')) return
    await api.post(`/chats/${chatActivo.id}/cerrar`)
    toast.success('Chat cerrado')
    setChatActivo(null)
    cargarChats()
  }

  function abrirEdicionContacto() {
    const c = chatActivo?.Contacto
    if (!c) return
    setEditForm({
      nombre: c.nombre || '',
      email:  c.email  || '',
      fuente: c.fuente || '',
      notas:  c.notas  || '',
      tags:   [...(c.tags || [])],
    })
    setEditTagInput('')
    setShowEditContacto(true)
  }

  async function guardarContacto() {
    setEditGuardando(true)
    try {
      const { data } = await api.put(`/contactos/${chatActivo.Contacto.id}`, editForm)
      const contactoActualizado = { ...chatActivo.Contacto, ...data }
      setChatActivo(prev => ({ ...prev, Contacto: contactoActualizado }))
      setChats(prev => prev.map(c =>
        c.id === chatActivo.id ? { ...c, Contacto: contactoActualizado } : c
      ))
      setShowEditContacto(false)
      toast.success('Contacto actualizado')
    } catch {
      toast.error('Error al guardar')
    } finally {
      setEditGuardando(false)
    }
  }

  const archivarChat = async (chatId) => {
    await api.post(`/chats/${chatId}/archivar`)
    setChats(prev => prev.filter(c => c.id !== chatId))
    if (chatActivo?.id === chatId) setChatActivo(null)
    toast.success('Chat archivado')
    setMenuChat(null)
  }

  const moverPapelera = async (chatId) => {
    await api.post(`/chats/${chatId}/papelera`)
    setChats(prev => prev.filter(c => c.id !== chatId))
    if (chatActivo?.id === chatId) setChatActivo(null)
    toast('🗑️ Movido a la papelera')
    setMenuChat(null)
  }

  const restaurarChat = async (chatId) => {
    await api.post(`/chats/${chatId}/restaurar`)
    setChats(prev => prev.filter(c => c.id !== chatId))
    if (chatActivo?.id === chatId) setChatActivo(null)
    toast.success('Chat restaurado al inbox')
    setMenuChat(null)
  }

  const eliminarDefinitivo = async (chatId) => {
    if (!window.confirm('¿Eliminar permanentemente? Esto borrará todos los mensajes y no se puede deshacer.')) return
    await api.delete(`/chats/${chatId}`)
    setChats(prev => prev.filter(c => c.id !== chatId))
    if (chatActivo?.id === chatId) setChatActivo(null)
    toast.success('Chat eliminado definitivamente')
    setMenuChat(null)
  }

  const cambiarPrioridad = async (prioridad) => {
    await api.post(`/chats/${chatActivo.id}/prioridad`, { prioridad })
  }

  const agregarNota = async () => {
    if (!nuevaNota.trim()) return
    const { data } = await api.post(`/chats/${chatActivo.id}/notas`, { texto: nuevaNota })
    setNotas([data, ...notas])
    setNuevaNota('')
  }

  const crearDeal = async () => {
    if (!dealForm.titulo || !dealForm.pipelineId || !dealForm.etapaId) {
      toast.error('Completa título, pipeline y etapa')
      return
    }
    try {
      await api.post('/pipelines/negocios', {
        titulo: dealForm.titulo,
        pipelineId: dealForm.pipelineId,
        etapaId: dealForm.etapaId,
        valor: parseFloat(dealForm.valor) || 0,
        contactoId: chatActivo.Contacto?.id,
      })
      toast.success('✅ Negocio creado en el pipeline')
      setShowCrearDeal(false)
      setDealForm({ titulo: '', pipelineId: '', etapaId: '', valor: '' })
    } catch (e) { toast.error(e.response?.data?.error || 'Error al crear negocio') }
  }

  const insertarRapida = (rapida) => {
    setTexto(rapida.contenido)
    setShowRapidas(false)
    textareaRef.current?.focus()
    api.post(`/respuestas/rapidas/${rapida.id}/usar`).catch(() => {})
  }

  const handleTextChange = (e) => {
    setTexto(e.target.value)
    setShowRapidas(e.target.value.startsWith('/') && e.target.value.length > 0)
  }

  // Filtros combinados
  const filtrados = chats.filter(c => {
    if (buscar) {
      const q = buscar.toLowerCase().replace(/^\+/, '')
      const nombre = nombreMostrar(c.Contacto).toLowerCase()
      const telefono = (c.Contacto?.telefono || '').replace(/^\+/, '')
      if (!nombre.includes(q) && !telefono.includes(q)) return false
    }
    if (filtroExtra === 'sin_leer')   return c.noLeidos > 0
    if (filtroExtra === 'bot')        return c.modoBot
    if (filtroExtra === 'sin_agente') return !c.agenteId && !c.modoBot
    return true
  })

  const rapidasFiltradas = respuestasRapidas.filter(r => r.comando.startsWith(texto.split(' ')[0]))

  const fmtHora = (d) => d ? new Date(d).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : ''
  const fmtFecha = (d) => {
    if (!d) return ''
    const dt = new Date(d)
    const hoy = new Date()
    if (dt.toDateString() === hoy.toDateString()) return fmtHora(d)
    return dt.toLocaleDateString('es', { day: '2-digit', month: 'short' })
  }
  const inicial = (n) => (n || '?')[0].toUpperCase()

  const totalNoLeidos = chats.reduce((s, c) => s + (c.noLeidos || 0), 0)

  return (
    <div className={`chat-layout ${!chatActivo ? 'no-right' : ''} ${showCatalogo ? 'with-catalog' : ''} ${isMobile && chatActivo ? 'mobile-open' : ''} ${chatActivo && !showRightPanel && !isMobile ? 'right-hidden' : ''}`}>

      {/* SIDEBAR CHATS */}
      <div className="chat-sidebar">

        {/* Header mobile-only */}
        <div className="mobile-inbox-header">
          <img src="/logo.png" width={28} height={28} style={{ borderRadius: 8, flexShrink: 0, objectFit: 'contain' }} alt="" />
          <span style={{ flex: 1, fontWeight: 700, fontSize: 16 }}>MixCRM</span>
          {totalNoLeidos > 0 && (
            <span style={{ background: '#ef4444', color: 'white', fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 20 }}>
              {totalNoLeidos}
            </span>
          )}
        </div>

        {/* ── Selector de Inbox ── */}
        <div style={{ position: 'relative', padding: '10px 10px 0' }}>
          <button onClick={() => setShowInboxMenu(v => !v)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
              background: 'var(--bg-soft,#f1f5f9)', border: '1px solid var(--border)', borderRadius: 10,
              cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>
            <span style={{ fontSize: 15 }}>
              {inboxFiltro.tipo === 'todos' ? '📥' : inboxFiltro.tipo === 'negocio' ? '🏢' : '📱'}
            </span>
            <span style={{ flex: 1 }}>{inboxFiltro.nombre}</span>
            {inboxFiltro.tipo !== 'todos' && (
              <span onClick={e => { e.stopPropagation(); setInboxFiltro({ tipo: 'todos', id: null, nombre: 'Todos los chats' }) }}
                style={{ fontSize: 11, color: 'var(--text-muted)', padding: '1px 5px', borderRadius: 8, background: 'var(--bg)' }}>✕</span>
            )}
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>▾</span>
          </button>
          {showInboxMenu && (
            <div onClick={() => setShowInboxMenu(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
          )}
          {showInboxMenu && (
            <div style={{ position: 'absolute', top: '100%', left: 10, right: 10, zIndex: 50, marginTop: 4,
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,.12)', overflow: 'hidden' }}>
              {/* Todos */}
              <div onClick={() => { setInboxFiltro({ tipo: 'todos', id: null, nombre: 'Todos los chats' }); setShowInboxMenu(false) }}
                style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600,
                  background: inboxFiltro.tipo === 'todos' ? 'var(--bg-active)' : 'transparent' }}>
                📥 Todos los chats
                {totalNoLeidos > 0 && <span style={{ marginLeft: 'auto', background: '#ef4444', color: 'white', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 20 }}>{totalNoLeidos}</span>}
              </div>
              {negocios.length > 0 && (
                <>
                  <div style={{ padding: '6px 14px 2px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, borderTop: '1px solid var(--border)' }}>Negocios</div>
                  {negocios.map(n => (
                    <div key={n.id}>
                      <div onClick={() => { setInboxFiltro({ tipo: 'negocio', id: n.id, nombre: n.nombre }); setShowInboxMenu(false) }}
                        style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                          background: inboxFiltro.tipo === 'negocio' && inboxFiltro.id === n.id ? 'var(--bg-active)' : 'transparent' }}>
                        <span style={{ fontSize: 16 }}>{n.emoji}</span>
                        <span style={{ flex: 1, fontWeight: 600 }}>{n.nombre}</span>
                        {n.chatsAbiertos > 0 && <span style={{ background: '#25D366', color: 'white', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 20 }}>{n.chatsAbiertos}</span>}
                      </div>
                      {/* Sub-números del negocio */}
                      {(n.Sesions || []).map(s => (
                        <div key={s.id} onClick={() => { setInboxFiltro({ tipo: 'sesion', id: s.id, nombre: s.nombre }); setShowInboxMenu(false) }}
                          style={{ padding: '6px 14px 6px 36px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                            background: inboxFiltro.tipo === 'sesion' && inboxFiltro.id === s.id ? 'var(--bg-active)' : 'transparent' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.status === 'conectado' ? '#22c55e' : '#94a3b8', flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>{s.nombre}</span>
                          {s.chatsAbiertos > 0 && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{s.chatsAbiertos}</span>}
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Vistas: Inbox / Archivados / Papelera ── */}
        <div style={{ display: 'flex', gap: 4, padding: '8px 10px 0', borderTop: '1px solid var(--border)' }}>
          {[
            { id: 'inbox',     label: '📥 Inbox' },
            { id: 'grupos',    label: '👥 Grupos' },
            { id: 'archivados',label: '📦 Archivados' },
            { id: 'papelera',  label: '🗑️ Papelera' },
          ].map(v => (
            <button key={v.id} onClick={() => { setVista(v.id); setChatActivo(null) }}
              style={{ flex: 1, padding: '5px 4px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                background: vista === v.id ? 'var(--primary)' : 'var(--bg)',
                color: vista === v.id ? 'white' : 'var(--text-soft)' }}>
              {v.label}
            </button>
          ))}
        </div>

        {/* ── Tabs: Todos / Míos / En espera (solo en inbox normal) ── */}
        {vista === 'inbox' && (
          <div className="chat-tabs" style={{ marginTop: 8 }}>
            {[
              { id: 'todos', label: 'Todos' },
              { id: 'mios',  label: 'Míos' },
              { id: 'espera',label: 'En espera' },
            ].map(t => (
              <div key={t.id} className={`chat-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
                {t.label}
                {t.id === 'todos' && totalNoLeidos > 0 && (
                  <span style={{ background: '#ef4444', color: 'white', fontSize: 10, fontWeight: 800, padding: '1px 5px', borderRadius: 20, marginLeft: 4 }}>
                    {totalNoLeidos}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="chat-search">
          <input placeholder="🔍 Buscar..." value={buscar} onChange={e => setBuscar(e.target.value)} />
        </div>

        {/* Filtros rápidos */}
        <div style={{ display: 'flex', gap: 4, padding: '0 10px 8px', overflowX: 'auto' }}>
          {[
            { id: 'todos',      label: 'Todos' },
            { id: 'sin_leer',   label: '🔴 Sin leer' },
            { id: 'bot',        label: '🤖 Bot activo' },
            { id: 'sin_agente', label: '⏳ Sin agente' },
          ].map(f => (
            <button key={f.id} onClick={() => setFiltroExtra(f.id)}
              style={{ padding: '3px 10px', borderRadius: 20, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 600,
                background: filtroExtra === f.id ? 'var(--primary)' : 'var(--bg)',
                color:      filtroExtra === f.id ? 'white' : 'var(--text-soft)' }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Cierre del menú contextual al hacer click fuera */}
        {menuChat && <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setMenuChat(null)} />}

        <div className="chat-list">
          {filtrados.map(chat => (
            <div key={chat.id} style={{ position: 'relative' }}>
              <div
                className={`chat-item ${chatActivo?.id === chat.id ? 'active' : ''} ${chat.noLeidos > 0 ? 'unread' : ''}`}
                onClick={() => {
                  setChatActivo(chat)
                  setChats(prev => prev.map(c => c.id === chat.id ? { ...c, noLeidos: 0 } : c))
                  clearBadge()
                  setMenuChat(null)
                }}
                style={{ paddingRight: 36 }}>
                <div className="avatar" style={{ background: chat.Sesion?.color || 'var(--primary)' }}>
                  {chat.Contacto?.avatar
                    ? <img src={mediaUrl(chat.Contacto.avatar)} alt="" />
                    : inicial(chat.Contacto?.nombre)}
                </div>
                <div className="chat-item-info">
                  <div className="chat-item-name">
                    {PRIORIDADES[chat.prioridad]} {nombreMostrar(chat.Contacto)}
                  </div>
                  <div className="chat-item-preview">
                    {chat.modoBot ? '🤖 ' : chat.agente ? '👤 ' : '⏳ '}
                    {chat.ultimoMensaje || 'Sin mensajes'}
                  </div>
                </div>
                <div className="chat-item-meta">
                  <span className="chat-item-time">{fmtFecha(chat.ultimaActividad)}</span>
                  {chat.noLeidos > 0 && <span className="chat-item-badge">{chat.noLeidos}</span>}
                </div>
              </div>

              {/* Botón "⋯" que aparece al hacer hover */}
              <button
                onClick={e => { e.stopPropagation(); setMenuChat(menuChat === chat.id ? null : chat.id) }}
                title="Opciones"
                style={{
                  position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  background: menuChat === chat.id ? 'var(--primary)' : 'var(--bg)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  width: 24, height: 24, cursor: 'pointer', fontSize: 14, lineHeight: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: menuChat === chat.id ? 'white' : 'var(--text-muted)',
                  opacity: chatActivo?.id === chat.id || menuChat === chat.id ? 1 : 0,
                  transition: 'opacity 0.15s',
                }}
                className="chat-item-menu-btn"
              >⋯</button>

              {/* Menú desplegable */}
              {menuChat === chat.id && (
                <div style={{
                  position: 'absolute', right: 8, top: '100%', zIndex: 99,
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.15)',
                  minWidth: 180, overflow: 'hidden',
                }}>
                  {vista === 'inbox' && (
                    <>
                      <button onClick={() => archivarChat(chat.id)} style={MENU_ITEM_STYLE}>
                        📦 Archivar
                        <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>Ocultar del inbox</span>
                      </button>
                      <button onClick={() => moverPapelera(chat.id)} style={{ ...MENU_ITEM_STYLE, color: '#ef4444' }}>
                        🗑️ Mover a papelera
                      </button>
                    </>
                  )}
                  {vista === 'archivados' && (
                    <>
                      <button onClick={() => restaurarChat(chat.id)} style={MENU_ITEM_STYLE}>
                        📥 Restaurar al inbox
                      </button>
                      <button onClick={() => moverPapelera(chat.id)} style={{ ...MENU_ITEM_STYLE, color: '#ef4444' }}>
                        🗑️ Mover a papelera
                      </button>
                    </>
                  )}
                  {vista === 'papelera' && (
                    <>
                      <button onClick={() => restaurarChat(chat.id)} style={MENU_ITEM_STYLE}>
                        ↩️ Restaurar al inbox
                      </button>
                      <button onClick={() => eliminarDefinitivo(chat.id)} style={{ ...MENU_ITEM_STYLE, color: '#ef4444', fontWeight: 700 }}>
                        ❌ Eliminar definitivamente
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
          {filtrados.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">
                {vista === 'papelera' ? '🗑️' : vista === 'archivados' ? '📦' : vista === 'grupos' ? '👥' : '📭'}
              </div>
              <p>{vista === 'papelera' ? 'La papelera está vacía' : vista === 'archivados' ? 'Sin chats archivados' : vista === 'grupos' ? 'Aún no hay grupos con mensajes' : 'Sin chats'}</p>
            </div>
          )}
        </div>
      </div>

      {/* CONVERSACIÓN */}
      <div className="chat-conversation">
        {!chatActivo ? (
          <div className="chat-empty">
            <div className="chat-empty-icon">💬</div>
            <h3>Selecciona un chat</h3>
            <p>Los mensajes aparecerán aquí · Usa <kbd style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px', fontSize: 12 }}>Ctrl+K</kbd> para buscar</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              {/* Botón volver — solo mobile */}
              <button className="mobile-back-btn" onClick={() => { setChatActivo(null); setShowRightPanel(false) }}>‹</button>

              {/* Identidad del contacto (tap abre info en mobile) */}
              <button className="chat-header-id" onClick={() => { if (isMobile) setShowRightPanel(true) }} title="Ver info del contacto">
                <div className="avatar chat-header-avatar">
                  {chatActivo.Contacto?.avatar
                    ? <img src={mediaUrl(chatActivo.Contacto.avatar)} alt="" />
                    : inicial(chatActivo.Contacto?.nombre)}
                </div>
                <div style={{ minWidth: 0, textAlign: 'left' }}>
                  <div className="chat-header-name">
                    <span className="prio-dot" style={{ background: PRIO_META[chatActivo.prioridad || 'normal'].color }} title={`Prioridad: ${PRIO_META[chatActivo.prioridad || 'normal'].label}`} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nombreMostrar(chatActivo.Contacto)}
                    </span>
                  </div>
                  <div className="chat-header-sub">
                    {numeroMostrar(chatActivo.Contacto)}{chatActivo.Sesion?.nombre ? ` · ${chatActivo.Sesion.nombre}` : ''}
                  </div>
                </div>
              </button>

              <div className="chat-header-actions">
                {/* Acción primaria: estado bot / tomar */}
                {chatActivo.modoBot ? (
                  <button className="btn btn-sm btn-primary" onClick={tomar} title="Tomar la conversación">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    <span className="btn-label">Tomar</span>
                  </button>
                ) : (
                  <button className="btn btn-sm btn-outline" onClick={activarBot} title="Activar el bot">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="8.5" cy="16" r="1.2" fill="currentColor" stroke="none" /><circle cx="15.5" cy="16" r="1.2" fill="currentColor" stroke="none" /><path d="M12 7V4M12 4h-2M12 4h2" /></svg>
                    <span className="btn-label">Bot</span>
                  </button>
                )}

                {/* Cerrar — visible en desktop */}
                <button className="btn btn-sm btn-danger chat-hide-mobile" onClick={cerrar}>Cerrar</button>

                {/* Toggle panel derecho — solo desktop */}
                <button
                  className="icon-btn chat-hide-mobile"
                  onClick={() => setShowRightPanel(v => !v)}
                  title={showRightPanel ? 'Ocultar panel' : 'Mostrar info'}
                  style={{ opacity: showRightPanel ? 1 : 0.5 }}
                >
                  {showRightPanel ? (
                    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1.5" y="1.5" width="17" height="17" rx="2.5"/>
                      <line x1="13" y1="1.5" x2="13" y2="18.5"/>
                      <polyline points="16,8 18,10 16,12"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1.5" y="1.5" width="17" height="17" rx="2.5"/>
                      <line x1="13" y1="1.5" x2="13" y2="18.5"/>
                      <polyline points="11,8 9,10 11,12"/>
                    </svg>
                  )}
                </button>

                {/* Menú de más opciones (⋮) */}
                <div style={{ position: 'relative' }}>
                  <button className="icon-btn" onClick={() => setMenuHeader(v => !v)} title="Más opciones" aria-label="Más opciones">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg>
                  </button>
                  {menuHeader && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setMenuHeader(false)} />
                      <div className="header-menu">
                        <div className="header-menu-label">Prioridad</div>
                        {Object.entries(PRIO_META).map(([key, m]) => (
                          <button key={key} style={MENU_ITEM_STYLE}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            onClick={() => { cambiarPrioridad(key); setMenuHeader(false) }}>
                            <span className="prio-dot" style={{ background: m.color }} /> {m.label}
                            {(chatActivo.prioridad || 'normal') === key && <span style={{ marginLeft: 'auto', color: 'var(--primary)' }}>✓</span>}
                          </button>
                        ))}
                        <div className="header-menu-sep" />
                        {historial.length > 0 && (
                          <button style={MENU_ITEM_STYLE}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            onClick={() => { setShowHistorial(h => !h); setMenuHeader(false) }}>
                            🕐 Historial
                            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>{historial.length}</span>
                          </button>
                        )}
                        <button style={MENU_ITEM_STYLE}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          onClick={() => { setShowCatalogo(c => !c); setMenuHeader(false) }}>
                          📦 Catálogo de productos
                        </button>
                        <button style={MENU_ITEM_STYLE}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          onClick={() => { setShowNotas(n => !n); setMenuHeader(false) }}>
                          📝 Notas internas
                        </button>
                        <button className="header-menu-mobile" style={MENU_ITEM_STYLE}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          onClick={() => { setShowRightPanel(true); setMenuHeader(false) }}>
                          ℹ️ Info del contacto
                        </button>
                        <button className="header-menu-mobile" style={MENU_ITEM_STYLE}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          onClick={() => { abrirEdicionContacto(); setMenuHeader(false) }}>
                          ✏️ Editar contacto
                        </button>
                        <div className="header-menu-sep header-menu-mobile" />
                        <button className="header-menu-mobile" style={{ ...MENU_ITEM_STYLE, color: '#ef4444', fontWeight: 600 }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          onClick={() => { cerrar(); setMenuHeader(false) }}>
                          ✕ Cerrar chat
                        </button>

                        {/* Gestión del chat — solo mobile */}
                        <div className="header-menu-sep header-menu-mobile" />
                        {vista === 'inbox' && <>
                          <button className="header-menu-mobile" style={MENU_ITEM_STYLE}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            onClick={() => { archivarChat(chatActivo.id); setMenuHeader(false) }}>
                            📦 Archivar chat
                          </button>
                          <button className="header-menu-mobile" style={{ ...MENU_ITEM_STYLE, color: '#ef4444' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            onClick={() => { moverPapelera(chatActivo.id); setMenuHeader(false) }}>
                            🗑️ Mover a papelera
                          </button>
                        </>}
                        {vista === 'archivados' && <>
                          <button className="header-menu-mobile" style={MENU_ITEM_STYLE}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            onClick={() => { restaurarChat(chatActivo.id); setMenuHeader(false) }}>
                            📥 Restaurar al inbox
                          </button>
                          <button className="header-menu-mobile" style={{ ...MENU_ITEM_STYLE, color: '#ef4444' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            onClick={() => { moverPapelera(chatActivo.id); setMenuHeader(false) }}>
                            🗑️ Mover a papelera
                          </button>
                        </>}
                        {vista === 'papelera' && <>
                          <button className="header-menu-mobile" style={MENU_ITEM_STYLE}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            onClick={() => { restaurarChat(chatActivo.id); setMenuHeader(false) }}>
                            ↩️ Restaurar al inbox
                          </button>
                          <button className="header-menu-mobile" style={{ ...MENU_ITEM_STYLE, color: '#ef4444', fontWeight: 700 }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            onClick={() => { eliminarDefinitivo(chatActivo.id); setMenuHeader(false) }}>
                            ❌ Eliminar definitivamente
                          </button>
                        </>}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Historial inline */}
            {showHistorial && historial.length > 0 && (
              <div style={{ background: '#d9770622', borderBottom: '1px solid #d9770644', padding: '8px 16px', fontSize: 12 }}>
                <strong>🕐 Conversaciones anteriores de este contacto:</strong>
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  {historial.slice(0, 5).map(h => (
                    <span key={h.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 20, padding: '2px 10px', cursor: 'pointer' }}
                      onClick={() => {
                        setChatActivo(h)
                        setShowHistorial(false)
                      }}>
                      {fmtFecha(h.createdAt)} · {h.status}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="messages-area" ref={messagesAreaRef}>
              {mensajes.map((m, i) => (
                <div key={m.id || i} className={`message ${m.tipo}`}>
                  {m.tipo === 'saliente_bot'     && <span className="message-tag tag-bot">🤖 BOT</span>}
                  {m.tipo === 'saliente_sistema' && <span className="message-tag tag-system">📢 SISTEMA</span>}
                  {m.tipo === 'saliente_agente'  && m.autorNombre && (
                    <div className="text-sm text-soft" style={{ marginBottom: 2 }}>{m.autorNombre}</div>
                  )}
                  {m.mediaUrl && m.mediaTipo === 'image' && (
                    <img
                      src={mediaUrl(m.mediaUrl)} alt=""
                      onClick={() => setLightboxImg(mediaUrl(m.mediaUrl))}
                      style={{ maxWidth: 250, borderRadius: 8, marginBottom: 6, display: 'block', cursor: 'zoom-in' }}
                    />
                  )}
                  {m.mediaUrl && m.mediaTipo === 'video' && (
                    <video src={mediaUrl(m.mediaUrl)} controls style={{ maxWidth: 250, borderRadius: 8, marginBottom: 6 }} />
                  )}
                  {m.mediaUrl && m.mediaTipo === 'audio' && (
                    <audio src={mediaUrl(m.mediaUrl)} controls style={{ width: 220, marginBottom: 6 }} />
                  )}
                  {m.mediaUrl && m.mediaTipo === 'document' && (
                    <a href={mediaUrl(m.mediaUrl)} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                        background: 'rgba(0,0,0,.07)', borderRadius: 8, marginBottom: 6, textDecoration: 'none', color: 'inherit' }}>
                      📄 {m.mediaNombre || 'Descargar archivo'}
                    </a>
                  )}
                  {renderTexto(m.texto)}
                  <div className="message-meta">
                    {fmtHora(m.createdAt)}
                    {m.tipo.startsWith('saliente') && (
                      <span className={`msg-tick${m.estado === 'leido' ? ' tick-leido' : m.estado === 'fallido' ? ' tick-fallido' : ''}`}>
                        {m.estado === 'fallido'   ? '✕'
                         : m.estado === 'pendiente' ? '○'
                         : m.estado === 'leido' || m.estado === 'entregado' ? '✓✓'
                         : '✓'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            {showRapidas && rapidasFiltradas.length > 0 && (
              <div style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)', maxHeight: 200, overflowY: 'auto' }}>
                {rapidasFiltradas.map(r => (
                  <div key={r.id} style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid var(--bg)' }}
                    onClick={() => insertarRapida(r)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <div className="font-semibold text-sm"><code>{r.comando}</code> · {r.titulo}</div>
                    <div className="text-sm text-muted" style={{ marginTop: 2 }}>{r.contenido?.slice(0, 80)}...</div>
                  </div>
                ))}
              </div>
            )}

            {/* Preview de archivo antes de enviar */}
            {archivoPreview && (
              <div style={{ padding: '8px 14px', background: 'var(--bg)', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                {archivoPreview.tipo === 'image' && (
                  <img src={archivoPreview.url} alt="" style={{ height: 60, borderRadius: 6, objectFit: 'cover' }} />
                )}
                {archivoPreview.tipo === 'video' && (
                  <video src={archivoPreview.url} style={{ height: 60, borderRadius: 6 }} />
                )}
                {archivoPreview.tipo === 'audio' && (
                  <audio src={archivoPreview.url} controls style={{ height: 36 }} />
                )}
                {archivoPreview.tipo === 'document' && (
                  <span style={{ fontSize: 13 }}>📄 {archivoPreview.file.name}</span>
                )}
                <div style={{ flex: 1 }} />
                <button className="composer-btn" onClick={cancelarArchivo} title="Cancelar">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            )}

            <div className="message-input-area">
              {/* Input general (documentos + media) */}
              <input ref={fileInputRef} type="file" style={{ display: 'none' }}
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                onChange={seleccionarArchivo} />
              {/* Input galería — solo imágenes, abre galería en Android */}
              <input ref={galleryInputRef} type="file" style={{ display: 'none' }}
                accept="image/*"
                onChange={seleccionarArchivo} />
              {/* Adjuntar en desktop · Galería en mobile */}
              <button className="composer-btn chat-hide-mobile" title="Adjuntar archivo"
                onClick={() => fileInputRef.current?.click()}>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
              <button className="mobile-info-btn composer-btn" title="Galería"
                onClick={() => galleryInputRef.current?.click()}>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </button>
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder={archivoPreview ? 'Añade un pie de foto...' : isMobile ? 'Escribe un mensaje...' : 'Escribe un mensaje... (usa / para respuestas rápidas)'}
                value={texto}
                onChange={handleTextChange}
                onPaste={manejarPaste}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    archivoPreview ? enviarArchivo() : enviar()
                  }
                }}
              />
              <button className="composer-btn" style={{ marginRight: 4 }} title="Catálogo de productos"
                onClick={() => setShowCatalogo(!showCatalogo)}>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
              </button>
              <button className="send-btn" onClick={archivoPreview ? enviarArchivo : enviar} disabled={enviando} title="Enviar">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>

      {/* PANEL DERECHO mejorado con tabs */}
      {chatActivo && !showCatalogo && (showRightPanel || isMobile) && (
        <aside className={`chat-right${isMobile && showRightPanel ? ' mobile-panel-open' : ''}`} style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Topbar de cierre — solo mobile */}
          <div className="mobile-panel-topbar">
            <button onClick={() => setShowRightPanel(false)}>‹ Volver</button>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>Info contacto</span>
          </div>
          {/* Header del contacto */}
          <div
            onClick={abrirEdicionContacto}
            style={{ padding: '16px 14px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0, cursor: 'pointer' }}
            title="Editar contacto"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="avatar" style={{ width: 44, height: 44, background: 'var(--primary)', fontSize: 16 }}>
                {chatActivo.Contacto?.avatar
                  ? <img src={mediaUrl(chatActivo.Contacto.avatar)} alt="" />
                  : inicial(chatActivo.Contacto?.nombre)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{nombreMostrar(chatActivo.Contacto)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{numeroMostrar(chatActivo.Contacto)}</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.5 2.5a2.121 2.121 0 1 1 3 3L7 16l-4 1 1-4 10.5-10.5z"/>
              </svg>
            </div>
          </div>

          {/* Tabs del panel derecho */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            {[
              { id: 'contacto',    label: '👤' },
              { id: 'seguimiento', label: '📊' },
              { id: 'notas',       label: '📝' },
            ].map(t => (
              <button key={t.id} onClick={() => setTabDerecho(t.id)}
                title={t.id === 'contacto' ? 'Contacto' : t.id === 'seguimiento' ? 'Seguimiento' : 'Notas internas'}
                style={{ flex: 1, padding: '8px 4px', border: 'none', cursor: 'pointer', fontSize: 16,
                  borderBottom: `2px solid ${tabDerecho === t.id ? 'var(--primary)' : 'transparent'}`,
                  background: 'transparent', color: tabDerecho === t.id ? 'var(--primary)' : 'var(--text-muted)' }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Contenido del tab */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>

            {/* ── TAB CONTACTO ── */}
            {tabDerecho === 'contacto' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { label: 'Nombre', value: chatActivo.Contacto?.nombre },
                  { label: 'Teléfono', value: chatActivo.Contacto?.jid ? numeroMostrar(chatActivo.Contacto) : (chatActivo.Contacto?.telefono ? `+${chatActivo.Contacto.telefono}` : null) },
                  { label: 'Email', value: chatActivo.Contacto?.email },
                  { label: 'Fuente', value: chatActivo.Contacto?.fuente },
                  { label: 'Notas', value: chatActivo.Contacto?.notas },
                ].filter(f => f.value).map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 13 }}>{value}</div>
                  </div>
                ))}

                {chatActivo.Contacto?.tags?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Etiquetas</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {chatActivo.Contacto.tags.map(t => (
                        <span key={t} style={{ background: 'var(--primary)', color: 'white', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20 }}>{t}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Número que atendió */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 2 }}>Número WhatsApp</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: chatActivo.Sesion?.color || 'var(--primary)' }} />
                    {chatActivo.Sesion?.nombre}
                  </div>
                </div>

                {/* Historial rápido */}
                {historial.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Conversaciones anteriores ({historial.length})</div>
                    {historial.slice(0, 3).map(h => (
                      <div key={h.id} onClick={() => setChatActivo(h)}
                        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 4, cursor: 'pointer', fontSize: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: h.status === 'cerrado' ? '#22c55e' : '#f59e0b', fontWeight: 600 }}>{h.status}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{fmtFecha(h.createdAt)}</span>
                        </div>
                        <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{h.ultimoMensaje?.slice(0, 40) || '...'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── TAB SEGUIMIENTO ── */}
            {tabDerecho === 'seguimiento' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Datos del chat */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Estado del chat</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      { label: 'Prioridad', value: { baja: '🟢 Baja', normal: '🔵 Normal', alta: '🟡 Alta', urgente: '🔴 Urgente' }[chatActivo.prioridad] || chatActivo.prioridad },
                      { label: 'Estado', value: chatActivo.status },
                      { label: 'Agente', value: chatActivo.agente?.nombre || 'Sin asignar' },
                      { label: 'Abierto', value: fmtFecha(chatActivo.createdAt) },
                      { label: 'Última actividad', value: fmtFecha(chatActivo.ultimaActividad) },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--bg)' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                        <span style={{ fontWeight: 600 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Acciones rápidas */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Acciones rápidas</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button className="btn btn-sm btn-outline" style={{ width: '100%', textAlign: 'left' }}
                      onClick={() => { setShowCrearDeal(true); setDealForm(f => ({ ...f, titulo: `Chat con ${chatActivo.Contacto?.nombre || chatActivo.Contacto?.telefono}` })) }}>
                      💼 Crear negocio en pipeline
                    </button>
                    {chatActivo.modoBot ? (
                      <button className="btn btn-sm btn-outline" style={{ width: '100%', textAlign: 'left' }} onClick={tomar}>
                        👤 Tomar chat (desactivar bot)
                      </button>
                    ) : (
                      <button className="btn btn-sm btn-outline" style={{ width: '100%', textAlign: 'left' }} onClick={activarBot}>
                        🤖 Activar bot
                      </button>
                    )}
                    <button className="btn btn-sm" style={{ width: '100%', textAlign: 'left', color: '#ef4444', borderColor: '#fca5a5' }} onClick={cerrar}>
                      ✅ Cerrar chat
                    </button>
                  </div>
                </div>

                {/* Modal crear deal */}
                {showCrearDeal && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14, background: 'var(--bg)' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>💼 Nuevo negocio en pipeline</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input className="input" style={{ fontSize: 12 }} placeholder="Título del negocio" value={dealForm.titulo} onChange={e => setDealForm(f => ({ ...f, titulo: e.target.value }))} />
                      <select className="input" style={{ fontSize: 12 }} value={dealForm.pipelineId} onChange={e => setDealForm(f => ({ ...f, pipelineId: e.target.value, etapaId: '' }))}>
                        <option value="">— Selecciona pipeline —</option>
                        {pipelines.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      </select>
                      {etapas.length > 0 && (
                        <select className="input" style={{ fontSize: 12 }} value={dealForm.etapaId} onChange={e => setDealForm(f => ({ ...f, etapaId: e.target.value }))}>
                          <option value="">— Selecciona etapa —</option>
                          {etapas.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                        </select>
                      )}
                      <input className="input" style={{ fontSize: 12 }} placeholder="Valor estimado (USD)" type="number" value={dealForm.valor} onChange={e => setDealForm(f => ({ ...f, valor: e.target.value }))} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-primary" style={{ flex: 1 }} onClick={crearDeal}>Crear</button>
                        <button className="btn btn-sm" onClick={() => setShowCrearDeal(false)}>Cancelar</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── TAB NOTAS ── */}
            {tabDerecho === 'notas' && (
              <div>
                <textarea className="textarea" placeholder="Escribe una nota interna..." value={nuevaNota} onChange={e => setNuevaNota(e.target.value)} rows={3} style={{ fontSize: 13 }} />
                <button className="btn btn-sm btn-primary" style={{ marginTop: 8, width: '100%' }} onClick={agregarNota}>+ Agregar nota</button>
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {notas.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Sin notas aún</div>}
                  {notas.map(n => (
                    <div key={n.id} style={{ padding: '10px 12px', background: '#fefce8', border: '1px solid #fef08a', borderRadius: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700 }}>{n.autor?.nombre}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtFecha(n.createdAt)}</span>
                      </div>
                      <div style={{ fontSize: 13 }}>{n.texto}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      )}

      {/* PANEL CATÁLOGO */}
      {chatActivo && showCatalogo && (
        <aside className="chat-right" style={{ padding: 0, position: 'relative' }}>
          <CatalogoPicker
            chatId={chatActivo.id}
            sesionId={chatActivo.sesionId}
            onClose={() => setShowCatalogo(false)}
          />
        </aside>
      )}

      {/* ── LIGHTBOX IMAGEN ── */}
      {lightboxImg && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxImg(null)}
        >
          {/* Toolbar */}
          <div className="lightbox-toolbar" onClick={e => e.stopPropagation()}>
            <a
              href={lightboxImg}
              download
              className="lightbox-btn"
              title="Descargar"
              onClick={e => e.stopPropagation()}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </a>
            <button
              className="lightbox-btn"
              onClick={() => setLightboxImg(null)}
              title="Cerrar (Esc)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Imagen */}
          <img
            src={lightboxImg}
            alt=""
            className="lightbox-img"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* ── MODAL EDITAR CONTACTO ── */}
      {showEditContacto && chatActivo?.Contacto && (
        <div className="contact-edit-overlay" onClick={e => { if (e.target === e.currentTarget) setShowEditContacto(false) }}>
          <div className="contact-edit-panel animate-scale-in">

            {/* Header sticky */}
            <div className="contact-edit-header">
              <button className="contact-edit-back" onClick={() => setShowEditContacto(false)} title="Cancelar">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 10H5M10 5l-5 5 5 5"/>
                </svg>
              </button>
              <h2 style={{ flex: 1, margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Editar contacto</h2>
              <button className="contact-edit-save-btn" onClick={guardarContacto} disabled={editGuardando}>
                {editGuardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>

            {/* Banner con avatar */}
            <div className="contact-edit-banner">
              <div className="contact-edit-avatar">
                {chatActivo.Contacto.avatar
                  ? <img src={mediaUrl(chatActivo.Contacto.avatar)} alt="" />
                  : <span>{inicial(chatActivo.Contacto.nombre)}</span>}
              </div>
              <div className="contact-edit-banner-name">{nombreMostrar(chatActivo.Contacto)}</div>
              <div className="contact-edit-banner-phone">{numeroMostrar(chatActivo.Contacto)}</div>
            </div>

            {/* Campos */}
            <div className="contact-edit-fields">

              {/* Nombre */}
              <div className="contact-edit-field">
                <label>
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  Nombre
                </label>
                <input
                  className="contact-edit-input"
                  placeholder="Nombre completo"
                  value={editForm.nombre}
                  onChange={e => setEditForm(f => ({ ...f, nombre: e.target.value }))}
                />
              </div>

              {/* Email */}
              <div className="contact-edit-field">
                <label>
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Email
                </label>
                <input
                  className="contact-edit-input"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={editForm.email}
                  onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                />
              </div>

              {/* Fuente */}
              <div className="contact-edit-field">
                <label>
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Fuente
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
                  {['', 'WhatsApp', 'Web', 'Instagram', 'Facebook', 'Manual', 'Importado', 'Referido', 'Campaña'].map(s => {
                    const active = (editForm.fuente || '') === s
                    return (
                      <button
                        key={s || '__none__'}
                        type="button"
                        onClick={() => setEditForm(f => ({ ...f, fuente: s }))}
                        style={{
                          padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          border: `1.5px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                          background: active ? 'var(--primary)' : 'var(--bg)',
                          color: active ? 'white' : 'var(--text-soft)',
                          transition: 'all .15s',
                        }}
                      >
                        {s || 'Sin fuente'}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Etiquetas */}
              <div className="contact-edit-field">
                <label>
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
                  </svg>
                  Etiquetas
                </label>
                <div className="contact-edit-tags">
                  {editForm.tags?.map(t => (
                    <span key={t} className="contact-edit-tag">
                      {t}
                      <button onClick={() => setEditForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }))}>×</button>
                    </span>
                  ))}
                  <input
                    className="contact-edit-tag-input"
                    placeholder={editForm.tags?.length ? 'Añadir…' : 'Escribe y presiona Enter'}
                    value={editTagInput}
                    onChange={e => setEditTagInput(e.target.value)}
                    onKeyDown={e => {
                      if ((e.key === 'Enter' || e.key === ',') && editTagInput.trim()) {
                        e.preventDefault()
                        const tag = editTagInput.trim().replace(/,$/, '')
                        if (tag && !editForm.tags.includes(tag))
                          setEditForm(f => ({ ...f, tags: [...f.tags, tag] }))
                        setEditTagInput('')
                      }
                      if (e.key === 'Backspace' && !editTagInput && editForm.tags?.length)
                        setEditForm(f => ({ ...f, tags: f.tags.slice(0, -1) }))
                    }}
                  />
                </div>
              </div>

              {/* Notas */}
              <div className="contact-edit-field" style={{ borderBottom: 'none' }}>
                <label>
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2V4a2 2 0 0 0-2-2z"/>
                  </svg>
                  Notas
                </label>
                <textarea
                  className="contact-edit-input"
                  rows={3}
                  placeholder="Notas sobre este contacto…"
                  value={editForm.notas}
                  onChange={e => setEditForm(f => ({ ...f, notas: e.target.value }))}
                  style={{ resize: 'vertical', lineHeight: 1.5 }}
                />
              </div>

            </div>

            {/* Botón guardar bottom (mobile) */}
            <div style={{ padding: '12px 16px 24px' }}>
              <button
                className="contact-edit-save-bottom"
                onClick={guardarContacto}
                disabled={editGuardando}
              >
                {editGuardando ? 'Guardando…' : '✓ Guardar cambios'}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  )
}
